import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, RadioIcon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { profileCoordinates, publicReportStatusLabels, publicReports, reportCategories, type PublicReport, type PublicReportCategory } from '../../data/demandes';
type Filter = 'tous' | PublicReportCategory;
const filters: Filter[] = ['tous', 'danger', 'voirie', 'eclairage', 'arbre'];
const statusStyles: Record<PublicReport['status'], string> = {
  nouveau: 'bg-ville-lime text-ville-navy',
  'en-traitement': 'bg-ville-indigo-soft text-ville-indigo',
  resolue: 'bg-slate-100 text-slate-500'
};
function pinIcon(report: PublicReport, active: boolean): L.DivIcon {
  const color = reportCategories[report.category].color;
  const size = active ? 34 : 24;
  return L.divIcon({
    className: 'atna-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30 ${active ? 'ring-2 ring-ville-navy/20' : ''}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function PublicReportsMap() {
  const [filter, setFilter] = useState<Filter>('tous');
  const [selectedId, setSelectedId] = useState<string>(publicReports[0].id);
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const visible = useMemo(() => filter === 'tous' ? publicReports : publicReports.filter(report => report.category === filter), [filter]);
  const selected = visible.find(report => report.id === selectedId) ?? visible[0];
  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newCount = publicReports.filter(report => report.status === 'nouveau').length;
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: profileCoordinates,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    // Tuiles OpenStreetMap : gratuites et sans clé d'API.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: '#1a1a17',
      fillOpacity: 1
    }).bindTooltip('Votre adresse').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    visible.forEach(report => {
      L.marker([report.lat, report.lng], {
        icon: pinIcon(report, report.id === selected?.id),
        keyboard: true,
        title: `${reportCategories[report.category].label} — ${report.title}`,
        alt: `${reportCategories[report.category].label} — ${report.title}`,
        zIndexOffset: report.id === selected?.id ? 500 : 0
      }).on('click', () => setSelectedId(report.id)).on('keypress', () => setSelectedId(report.id)).addTo(layer);
    });
  }, [visible, selected?.id]);
  useEffect(() => {
    if (!mapRef.current || !selected) return;
    mapRef.current.panTo([selected.lat, selected.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selected?.id]);
  const step = (direction: -1 | 1) => {
    if (visible.length === 0) return;
    const next = (selectedIndex + direction + visible.length) % visible.length;
    setSelectedId(visible[next].id);
  };
  const toggleConfirm = (id: string) => {
    setConfirmedIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };
  return <section aria-labelledby="carte-publique-titre"
  /* `isolate` garde les couches Leaflet (contrôles, fiche flottante) dans leur propre
     contexte d'empilement, sinon elles passent au-dessus du dialogue ATNA. */ className="relative isolate z-0 overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-952" data-mp="2fisnhmi15hgj">
      <div className="px-5 pb-4 pt-5 sm:px-6" data-id="element-953" data-mp="21ihxsmsgi8wy">
        <div className="flex items-start justify-between gap-4" data-id="element-954" data-mp="1wk6ocd62q0f9">
          <div data-id="element-955" data-mp="1wmjevepwdj3x">
            <h2 id="carte-publique-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-956" data-mp="1sj4opv88vmcr">
              Signalements autour de vous
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-957" data-mp="3qxy29ovfr6gi">
              {publicReports.length} signalements actifs · {newCount} nouveaux aujourd’hui
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-958" data-mp="18olgn3vgwvbo">
            <RadioIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-959" data-mp="1if6c4ifuhjqd" />
            En direct
          </span>
        </div>

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-960" data-mp="1mhb3tbmqg6ad">
          {filters.map(option => {
          const active = filter === option;
          const label = option === 'tous' ? 'Tous' : reportCategories[option].label;
          return <li key={option} data-id="element-961" data-mp="uq57l6a4usqd">
                <button type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-962" data-mp="yr6z10xt60hs">
                  {option !== 'tous' && <span className="h-2 w-2 rounded-full" style={{
                backgroundColor: reportCategories[option].color
              }} aria-hidden="true" data-id="element-963" data-mp="p0lri0dkfzet" />}
                  {label}
                </button>
              </li>;
        })}
        </ul>
      </div>

      <div className="relative" data-id="element-964" data-mp="63kmwgnxc1be">
        <div ref={containerRef} role="application" aria-label="Carte des signalements publics à Brossard" className="h-[300px] w-full bg-ville-mist sm:h-[380px]" data-id="element-965" data-mp="63u35cj62u3w" />

        {selected ? <motion.div key={selected.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-ville-navy/10 sm:inset-x-4" data-id="element-966" data-mp="34ssuof8varvt">
            <div className="flex items-start gap-3" data-id="element-967" data-mp="1fbdw3e9uu7vc">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{
            backgroundColor: reportCategories[selected.category].color
          }} aria-hidden="true" data-id="element-968" data-mp="1fbxawm47kg55" />
              <div className="min-w-0 flex-1" data-id="element-969" data-mp="27xyu2xku5i7v">
                <div className="flex flex-wrap items-center gap-2" data-id="element-970" data-mp="1u137e2bnvs9c">
                  <h3 className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-971" data-mp="1yzd3cf57mww0">{selected.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyles[selected.status]}`} data-id="element-972" data-mp="ilep3o9crhev">
                    {publicReportStatusLabels[selected.status]}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-973" data-mp="1eb2vnkjonapo">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-974" data-mp="1a7n06rjcngek" />
                  <span className="truncate" data-id="element-975" data-mp="1593duq0ug2lo">{selected.address}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-400" data-id="element-976" data-mp="10as40fajts3g">
                  À {selected.distance} · {selected.reportedAgo}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" data-id="element-977" data-mp="1y0de36atc9rh">
                <button type="button" onClick={() => step(-1)} aria-label="Signalement précédent" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-978" data-mp="1t0ii4oc6ktzk">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-979" data-mp="2bwqs25g51t17" />
                </button>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400" data-id="element-980" data-mp="2jb14j0to1zky">
                  {selectedIndex + 1}/{visible.length}
                </span>
                <button type="button" onClick={() => step(1)} aria-label="Signalement suivant" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-981" data-mp="2t6cktnkq3eut">
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-982" data-mp="3bz6npj52ngio" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3" data-id="element-983" data-mp="3gyt1btotays0">
              <p className="flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-984" data-mp="3pxmvzeil1f2x">
                <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-985" data-mp="3pvk9h4gn242y" />
                {selected.confirmations + (confirmedIds.includes(selected.id) ? 1 : 0)} personnes le confirment
              </p>
              <button type="button" onClick={() => toggleConfirm(selected.id)} aria-pressed={confirmedIds.includes(selected.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${confirmedIds.includes(selected.id) ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-ville-navy text-white hover:bg-ville-navy/90'}`} data-id="element-986" data-mp="35ne2j4cm29bi">
                <ThumbsUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-987" data-mp="1u0w3khfsgk98" />
                {confirmedIds.includes(selected.id) ? 'Confirmé' : 'Je confirme'}
              </button>
            </div>
          </motion.div> : <div className="absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-xl shadow-ville-navy/10" data-id="element-988" data-mp="1rgr63mllcxe7">
            Aucun signalement dans cette catégorie pour le moment.
          </div>}
      </div>
    </section>;
}